import firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/firestore'

import { Customer, Payee, Ledger, Revenue, ILedger, Invoice, RevenueStatus, Receipt, Expense, Meter } from './models'
import { Serialize, DeserializeInto, Deserialize } from 'cerialize'

import { pad } from '../helpers/strings'
import Voucher from './models/voucher'

type QueryDocumentSnapshot = firebase.firestore.QueryDocumentSnapshot
type DocumentSnapshot = firebase.firestore.DocumentSnapshot
type SnapshotSerializer<T> = (snapshot: QueryDocumentSnapshot) => T

interface GetOption<T> {
  ignoreCache?: true
  cacheKey?: string
  filter?: (collection: firebase.firestore.CollectionReference) => firebase.firestore.Query
  serializer: SnapshotSerializer<T>
}

interface SnapshotSerializers {
  ledgers: SnapshotSerializer<Ledger>
  revenues: SnapshotSerializer<Revenue>
  invoices: SnapshotSerializer<Invoice>
  customers: SnapshotSerializer<Customer>
  receipts: SnapshotSerializer<Receipt>
  payees: SnapshotSerializer<Payee>
  meters: SnapshotSerializer<Meter>
  vouchers: SnapshotSerializer<Voucher>
}

/**
 * issue Invoice
 * issue Receipt
 * report Revenue
 * query Revenue List
 *
 * Momospace will connect to Firestore data set.
 */
export class Momospace {

  private app: firebase.app.App

  private db: firebase.firestore.Firestore

  private cache: { [key: string]: any } = {}

  private user?: firebase.User

  private serializers: SnapshotSerializers = {
    ledgers: (o): Ledger => {
      const json = { ...o.data(), id: o.id } as ILedger
      if (json.type === 'rev') {
        return Deserialize(json, Revenue) as Revenue
      } else if (json.type === 'exp') {
        return Deserialize(json, Expense) as Expense
      } else {
        return Deserialize(json, Ledger)
      }
    },
    revenues: (o): Revenue => {
      const json = { ...o.data(), id: o.id }
      return Deserialize(json, Revenue)
    },
    invoices: (o): Invoice => {
      const json = { ...o.data(), id: o.id }
      return Deserialize(json, Invoice)
    },
    customers: (o): Customer => ({
      ...(o.data() as Customer),
      code: o.id,
    }),
    payees: (o): Payee => {
      const json = o.data()
      return DeserializeInto(json, Payee, new Payee(o.id))
    },
    receipts: (o): Receipt => {
      const json = { ...o.data(), id: o.id }
      return Deserialize(json, Receipt)
    },
    meters: (o): Meter => {
      const json = { ...o.data(), id: o.id }
      return Deserialize(json, Meter)
    },
    vouchers: (o): Voucher => {
      const json = { ...o.data(), id: o.id }
      return Deserialize(json, Voucher)
    },
  }

  /**
   * TODO: Consider using custom claims
   *
   * @see https://firebase.google.com/docs/auth/admin/custom-claims
   */
  constructor() {
    const config = {
      apiKey: 'AIzaSyAVu5nVrOgXRZw61Z9dE01WnXhJFsjH6Ik',
      authDomain: 'momospace-fe22d.firebaseapp.com',
      databaseURL: 'https://momospace-fe22d.firebaseio.com',
      projectId: 'momospace-fe22d',
      storageBucket: 'momospace-fe22d.appspot.com',
      messagingSenderId: '753386285946',
    }
    this.app = firebase.initializeApp(config)

    this.db = this.app.firestore()
    console.log('💊 Initializing datasource')

    // Listen for Login statues.
    this.app.auth().onAuthStateChanged(async (user) => {
      // Make sure use logged in.
      if (!user) {
        const provider = new firebase.auth.GoogleAuthProvider()

        // Ask for additional scope for other APIs to invoke (if needed).

        const result = await this.app.auth().signInWithPopup(provider)
        if (!result.credential) {
          throw new Error('Login with Google Failed')
        }
        // Use this accessToken with Google APIs.
        const accessToken = result.credential
        //
        if (!result.user) {
          throw new Error('Unable to perform login with Google')
        }
        this.user = result.user
      } else {
        this.user = user
      }
    })
  }

  public async loggedIn(): Promise<firebase.User> {
    if (this.user) {
      return this.user
    }
    return new Promise((resolve, reject) => {
      const callMe = setInterval(() => {
        if (this.user) {
          resolve(this.user)
          clearInterval(callMe)
          return
        }
      }, 300)
    })
  }

  public async issueInvoice(revnueId: string) {
    // TODO: Implement me
  }

  public async issueReceipt(revnueId: string) {
    // TODO: Implement me
  }

  public async customers(): Promise<Customer[]> {
    return await this._get('customers', {
      serializer: this.serializers.customers,
    })
  }

  public async payees(): Promise<Payee[]> {
    return await this._get('payees', {
      serializer: this.serializers.payees,
    })
  }

  public async revenues(status?: RevenueStatus): Promise<any[]> {
    return await this._get('ledgers', {
      filter: (c) => {
        let q: any = c.where('type', '==', 'rev')
        if (status) {
          q = q.where('status', '==', status)
        }
        return q
      },
      cacheKey: `ledgers-rev-${status}`,
      serializer: this.serializers.revenues,
    })
  }

  public async ledgers(): Promise<Ledger[]> {
    return await this._get('ledgers', {
      filter: (c) => c.orderBy('last_updated', 'desc'),
      serializer: this.serializers.ledgers,
    })
  }

  public async invoices(): Promise<Invoice[]> {
    return await this._get('invoices', {
      filter: (c) => c.orderBy('last_updated', 'desc'),
      serializer: this.serializers.invoices,
    })
  }

  public async receipts(): Promise<Receipt[]> {
    return await this._get('receipts', {
      filter: (c) => c.orderBy('last_updated', 'desc'),
      serializer: this.serializers.receipts,
    })
  }

  public async meters(): Promise<Meter[]> {
    return await this._get('meters', {
      filter: (c) => c.orderBy('monthCode', 'desc'),
      serializer: this.serializers.meters,
    })
  }

  public async vouchers(): Promise<Voucher[]> {
    return await this._get('vouchers', {
      filter: (c) => c.orderBy('last_updated', 'desc'),
      serializer: this.serializers.vouchers,
    })
  }

  /* WRITE OPERATION */

  public async writeCustomer(customer: Customer) {
    const data: any = { ...customer }
    delete data.code
    await this.db.collection('customers').doc(customer.code).set(data)
    delete this.cache.customers
  }

  public async writePyee(payee: Payee) {
    const data: any = { ...payee }
    delete data.code
    await this.db.collection('payees').doc(payee.code).set(data)
    delete this.cache.payees
  }

  public async writeLedger(ledger: Ledger) {
    const json = Serialize(ledger)
    const collectRef = this.db.collection('ledgers')
    // update
    if (json.id) {
      const id = json.id
      delete json.id
      await collectRef.doc(id).set({
        ...json,
        last_updated: firebase.firestore.FieldValue.serverTimestamp(),
      })
    } else {
      delete json.id
      await collectRef.add({
        ...json,
        last_updated: firebase.firestore.FieldValue.serverTimestamp(),
      })
    }
    delete this.cache.ledgers
  }

  public async writeReceipt(receipt: Receipt): Promise<void> {
    const receiptsRef = this.db.collection('receipts')
    const ledgersRef = this.db.collection('ledgers')
    const json = {
      ...Serialize(receipt),
      last_updated: firebase.firestore.FieldValue.serverTimestamp(),
    }
    delete json.id
    await this.db.runTransaction(async (tx) => {
      let receiptId = receipt.id
      if (receiptId) {
        const docRef = receiptsRef.doc(receiptId)
        docRef.set(json)
        tx = tx.set(docRef, json)
      } else {
        const docRef = await receiptsRef.add(json)
        receiptId = docRef.id
        tx = tx.set(docRef, json)
      }

      // update
      for (const item of receipt.items) {
        const ledgerRef = ledgersRef.doc(item.id)
        const status: RevenueStatus = 'received'
        tx = tx.update(ledgerRef, { status })
      }
    })
    delete this.cache.receipts
  }

  public async writeMeter(meter: Meter): Promise<void> {
    const metersRef = this.db.collection('meters')
    const json = {
      ...Serialize(meter),
      last_updated: firebase.firestore.FieldValue.serverTimestamp(),
    }
    delete json.id
    let meterId = meter.id
    if (meterId) {
      const docRef = metersRef.doc(meterId)
      await docRef.set(json)
    } else {
      const docRef = await metersRef.add(json)
      meterId = docRef.id
    }
    delete this.cache.meters
  }

  public async writeVoucher(voucher: Voucher): Promise<void> {
    const vouchersRef = this.db.collection('vouchers')
    const json = {
      ...Serialize(voucher),
      last_updated: firebase.firestore.FieldValue.serverTimestamp(),
    }
    delete json.id
    let voucherId = voucher.id
    if (voucherId) {
      const docRef = vouchersRef.doc(voucherId)
      await docRef.set(json)
    } else {
      const docRef = await vouchersRef.add(json)
      voucherId = docRef.id
    }
    delete this.cache.vouchers
  }

  /* DELETE OPERATION */

  public async deleteReceipt(receiptId: string): Promise<void> {
    await this.db.collection('receipts').doc(receiptId).delete()
    delete this.cache.receipts
  }

  public async deleteInvoice(invoiceId: string): Promise<void> {
    await this.db.collection('invoices').doc(invoiceId).delete()
    delete this.cache.invoices
  }

  /* DOCUMENT NUMBERING */

  public async newDocNumber(type: 'invoices'|'receipts', year?: Date): Promise<string> {
    let ofYear = +(year || new Date()).getFullYear()    // expected 20xx
    if (ofYear > 2500) {
      ofYear -= 543
    }
    const latestDocNumber = await this._latestDocNumber(type, ofYear)
    console.log('Latest Doc Number', latestDocNumber)
    const prefix = `${type.toUpperCase()[0]}${ofYear}`
    let indexToStart = 0
    if (latestDocNumber) {
      // latestDocNumber found ...
      const lastIndex = +latestDocNumber.replace(prefix, '')
      indexToStart = lastIndex
    }
    // Forge new number
    // Step new number
    indexToStart += 1
    return `${prefix}${pad(indexToStart, 2, '0')}`
  }

  public async writeInvoice(invoice: Invoice): Promise<void> {
    const invoicesRef = this.db.collection('invoices')
    const ledgersRef = this.db.collection('ledgers')
    const json = {
      ...Serialize(invoice),
      last_updated: firebase.firestore.FieldValue.serverTimestamp(),
    }
    delete json.id
    await this.db.runTransaction(async (tx) => {
      let invoiceId = invoice.id
      if (invoiceId) {
        const docRef = invoicesRef.doc(invoiceId)
        docRef.set(json)
        tx = tx.set(docRef, json)
      } else {
        const docRef = await invoicesRef.add(json)
        invoiceId = docRef.id
        tx = tx.set(docRef, json)
      }

      // update
      for (const item of invoice.items) {
        const ledgerRef = ledgersRef.doc(item.id)
        const status: RevenueStatus = 'invoiced'
        tx = tx.update(ledgerRef, { status, invoice_id: invoiceId })
      }
    })
    delete this.cache.invoices
  }

  /* PRIVATE METHODS */

  private async _get<T = QueryDocumentSnapshot>(collection: string, options: GetOption<T>): Promise<T[]> {
    const opts = options
    console.log('Fetching ', collection, options)
    let cacheKey: string = ''
    if (!opts.ignoreCache) {
      cacheKey = opts.cacheKey || collection
      const output: T[]|undefined = this.cache[cacheKey]
      if (output) {
        console.log(':Cache HIT')
        return output
      }
    }
    console.log(':Cache MISSED')
    const collectionRef = this.db.collection(collection)
    const q = opts.filter && opts.filter(collectionRef) || collectionRef
    const snapshot = await q.get()
    const o = snapshot.docs.map(opts.serializer)
    if (cacheKey) {
      this.cache[cacheKey] = o
    }
    return o
  }

  private async _latestDocNumber(type: 'invoices'|'receipts', ofYear: number): Promise<string|undefined> {
    // FIXME: Honour given `ofYear` parameter.
    const pattern = `${type === 'invoices' ? 'I' : 'R'}${ofYear}_`
    const found: string[] = await this._get(type, {
      filter: (c) => c.where('code', '<=', pattern).orderBy('code', 'desc').limit(1),
      serializer: (o): string => {
        return o.data().code
      },
      ignoreCache: true,
    })
    return found.length > 0 ? found[0] : undefined
  }
}

export default new Momospace()
